/**
 * Tiny static Google-Maps preview as an <img>. Cheap to render in lists where
 * an interactive map would be overkill (and slow). One Maps Static API hit per
 * unique coord+zoom+size combination.
 */

const apiKey = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string) ?? '';

interface StaticMapProps {
  lat: number;
  lng: number;
  zoom?: number;
  width?: number;
  height?: number;
  className?: string;
}

// Same palette as the interactive MapView (sand land + ocean water).
const STYLES = [
  'feature:all|element:geometry|color:0xecf3fa',
  'feature:all|element:labels.text.fill|color:0x0a2b46',
  'feature:all|element:labels.text.stroke|color:0xecf3fa',
  'feature:water|element:geometry|color:0x6ea4d5',
  'feature:water|element:labels.text.fill|color:0xffffff',
  'feature:water|element:labels.text.stroke|color:0x1f5e98',
  'feature:landscape.natural|element:geometry|color:0xd2e4f3',
  'feature:road|element:geometry|color:0xffffff',
  'feature:road|element:geometry.stroke|color:0xa5c8e6',
  'feature:poi|element:labels|visibility:off',
  'feature:poi|element:geometry|color:0xd2e4f3',
  'feature:poi.park|element:geometry|color:0xa5c8e6',
  'feature:transit|visibility:off',
  'feature:administrative.country|element:geometry.stroke|color:0x3f7eb8|weight:1.2',
  'feature:administrative.country|element:labels.text.fill|color:0x0e3a5f',
  'feature:administrative.province|element:geometry.stroke|color:0xa5c8e6',
].map((s) => `style=${encodeURIComponent(s)}`).join('&');

export function StaticMap({ lat, lng, zoom = 6, width = 480, height = 200, className }: StaticMapProps) {
  if (!apiKey) {
    return (
      <div
        className={`grid place-items-center bg-ocean-50 text-xs text-ocean-700 ${className ?? ''}`}
        style={{ width: '100%', height }}
      >
        Karte unverfügbar
      </div>
    );
  }

  // 2x for crisp retina rendering, capped at API limit (640).
  const w  = Math.min(640, width  * 2);
  const h  = Math.min(640, height * 2);
  // Use a coral pin against the ocean water for strong contrast.
  const marker = `markers=${encodeURIComponent(`color:0xf25d33|size:small|${lat},${lng}`)}`;
  const src = `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=${zoom}&size=${w}x${h}&scale=2&${marker}&${STYLES}&key=${apiKey}`;

  return (
    <img
      src={src}
      alt=""
      width={width}
      height={height}
      loading="lazy"
      className={`block h-full w-full object-cover ${className ?? ''}`}
    />
  );
}
